import { useState } from 'react';
import './RightSidebar.styles.css';
import { Typography } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ConfirmModal } from '../../../../components/confirm-modal';
import { useMutation, useQuery, useQueryClient, useIsMutating } from '@tanstack/react-query';
import {
  confirmPublishRegulation,
  deleteDraftRegulation,
  fetchDraftRegulationById,
  requestDeleteRegulation,
  requestPublishRegulation,
} from '../../Draft.services.tsx';
import { useTranslation } from 'react-i18next';

import { toast } from 'react-toastify';
import { usePermissions } from '../../../../hooks/usePermissions.ts';
import { FiCheckSquare, FiEdit, FiSave, FiTrash, FiX, FiXSquare } from 'react-icons/fi'; // import { RiShareForwardLine } from 'react-icons/ri';
import { RiShareForwardLine } from 'react-icons/ri';
import { useAuth } from '../../../../context/AuthContext.tsx';
import classnames from 'classnames';
import { REGULATION_TYPE } from '../../../../constants/regulation-type.tsx';

const { Title, Text } = Typography;

export default function RightSidebar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isSaving = useIsMutating({ mutationKey: ['saveDraftRegulation'] }) > 0;
  const [showRequestPublication, setShowRequestPublication] = useState(false);
  const [showConfirmPublication, setShowConfirmPublication] = useState(false);
  const [showRejectPublication, setShowRejectPublication] = useState(false);
  const { data } = useQuery({
    queryKey: ['draftRegulation', id],
    queryFn: () => fetchDraftRegulationById(id),
    enabled: !!id,
    retry: false,
  });

  const { isAllowed, isAdmin, isRegularUser } = usePermissions(data?.type);

  const isView = location.pathname.includes('view');

  const requestPublishMutation = useMutation({
    mutationKey: ['publishDraftRegulation'],
    mutationFn: requestPublishRegulation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['draftRegulations'] });
      queryClient.invalidateQueries({ queryKey: ['draftRegulation'] });
      setShowRequestPublication(false);
    },
    onError: (error: Error) => {
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      /* @ts-ignore */
      toast.error(error.response?.data?.message || error.message);
    },
  });

  const confirmPublishMutation = useMutation({
    mutationKey: ['publishDraftRegulation'],
    mutationFn: confirmPublishRegulation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['draftRegulations'] });
      queryClient.invalidateQueries({ queryKey: ['draftRegulation'] });
    },
    onError: (error: Error) => {
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      /* @ts-ignore */
      toast.error(error.response?.data?.message || error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDraftRegulation,
    onSuccess: () => {
      navigate('/');
    },
    onError: () => {
      console.error('Failed to delete draft');
    },
  });

  const requestDeleteMutation = useMutation({
    mutationFn: requestDeleteRegulation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['draftRegulations'] });
      queryClient.invalidateQueries({ queryKey: ['draftRegulation'] });
    },
    onError: () => {
      console.error('Failed to request deletion');
    },
  });

  const handleCloseRequestPublication = () => setShowRequestPublication(false);
  const handleCloseConfirmPublication = () => setShowConfirmPublication(false);
  const handleCloseConfirmDeletion = () => setShowRejectPublication(false);

  const handleRequestPublication = (message: string) => {
    if (!id) return;
    requestPublishMutation.mutate({ id, data: { requestMessage: message } });
    setShowRequestPublication(false);
  };

  const handleConfirmPublication = (
    message: string,
    subject: string,
    groups: number[],
    notifications: boolean,
    isBodyHTML: boolean,
  ) => {
    if (!id) return;
    confirmPublishMutation.mutate({
      id,
      data: {
        requestMessage: message,
        title: notifications ? subject : null,
        distributionLists: notifications ? groups.map((item) => Number(item)) : null,
        isBodyHtml: isBodyHTML,
      },
    });
    setShowConfirmPublication(false);
  };
  const isInLeadership = data?.unitInCharge?.leadership?.includes(user?.id);

  const canSaveDraft =
    (!isView && data?.status !== 2 && isRegularUser) ||
    (!isView && data?.status !== 3 && isAdmin) ||
    (!isView && data?.status === 2 && isInLeadership);
  const canCancel = (!isView && isRegularUser) || (!isView && isAdmin);
  const canEditDraft =
    (data?.status === 1 && isView && isRegularUser) ||
    (data?.status === 2 && isView && isAllowed) ||
    (data?.status === 2 && isView && isAdmin);
  const canDeleteDraft =
    (data?.status === 1 && !isView && isRegularUser) ||
    (data?.status === 1 && isView && isRegularUser) ||
    (data?.status === 2 && isView && isRegularUser) ||
    (data?.status === 2 && isView && isAllowed) ||
    (data?.status === 2 && isView && isAdmin);
  const canRequestPublication = id && data?.status === 1 && isView;
  const canPublish =
    (id && data?.status === 2 && isAllowed) ||
    (id && data?.status === 2 && isAdmin) ||
    (id && data?.status === 2 && isInLeadership && data?.type === 3);
  const canDeclinePublish =
    (id && data?.status === 2 && isAllowed) ||
    (id && data?.status === 2 && isAdmin) ||
    (id && data?.status === 2 && isInLeadership && data?.type === 3);

  const handleConfirmDeletion = (message: string) => {
    requestDeleteMutation.mutate({ id, data: { declinedMessage: message } });
    setShowRejectPublication(false);
  };

  const handleSave = () => {
    if (isSaving) return;

    const form = document.getElementById('DraftRegulationForm') as HTMLFormElement | null;
    form?.requestSubmit();
  };

  const handleDeleteDraft = () => {
    if (!id) return;
    deleteMutation.mutate(id);
  };
  return (
    <>
      <div className="draft-right-sidebar-container">
        <Title style={{ padding: 0, margin: 0 }} level={4}>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-expect-error */}
          {REGULATION_TYPE[data?.type] as 1 | 2 | 3}
        </Title>
        {data?.regulationNumber && <Text type="secondary">Number: {data?.regulationNumber}</Text>}

        <div className="action-links">
          {canSaveDraft && (
            <div
              className={classnames('action-link', { disabled: isSaving })}
              onClick={handleSave}
              aria-disabled={isSaving}
            >
              <FiSave fontSize={18} />
              <span className="action-link-text">{t('common.save')}</span>
            </div>
          )}
          {canEditDraft && (
            <div className="action-link" onClick={() => navigate(`/regulation/draft/${id}`)}>
              <FiEdit fontSize={18} />
              <span className="action-link-text">{t('common.edit')}</span>
            </div>
          )}
          {canDeleteDraft && (
            <div className="action-link" onClick={handleDeleteDraft}>
              <FiTrash fontSize={18} />
              <span className="action-link-text">{t('common.delete-draft')}</span>
            </div>
          )}
          {canRequestPublication && (
            <div className="action-link" onClick={() => setShowRequestPublication(true)}>
              <FiCheckSquare fontSize={18} />
              <span className="action-link-text">{t('common.ready-approval')}</span>
            </div>
          )}
          {canPublish && (
            <div className="action-link" onClick={() => setShowConfirmPublication(true)}>
              <FiCheckSquare fontSize={18} />
              <span className="action-link-text">{t('common.confirm-regulation')}</span>
            </div>
          )}
          {canDeclinePublish && (
            <div className="action-link" onClick={() => setShowRejectPublication(true)}>
              <FiXSquare fontSize={18} />
              <span className="action-link-text">{t('common.reject-regulation')}</span>
            </div>
          )}
          {isView && id && (
            <div
              className="action-link"
              onClick={() => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                const subject = encodeURIComponent(`Entwurf: ${REGULATION_TYPE[data.type]}`);
                const url = `${window.location.origin}/regulation/draft/view/${data.id}`;
                const body = encodeURIComponent(`Link to the regulation:\n${url}`);
                window.location.href = `mailto:?subject=${subject}&body=${body}`;
              }}
            >
              <RiShareForwardLine fontSize={18} />
              <span className="action-link-text">{t('common.forward')}</span>
            </div>
          )}
          {canCancel && (
            <div className="action-link" onClick={() => navigate(-1)}>
              <FiX fontSize={18} />
              <span className="action-link-text">{t('common.cancel')}</span>
            </div>
          )}
        </div>
        {showRequestPublication && (
          <ConfirmModal
            simple
            validateMessage
            open={showRequestPublication}
            title={t('modals.confirm-request-publication')}
            message={t('modals.confirm-request-publication-message')}
            onClose={handleCloseRequestPublication}
            onConfirm={handleRequestPublication}
          />
        )}
        {showConfirmPublication && (
          <ConfirmModal
            open={showConfirmPublication}
            loadTemplate
            templateType="publish"
            regulationDraftId={id}
            title={t('modals.confirm-approve-publication')}
            message={t('modals.confirm-approve-publication-message')}
            subject={data?.title}
            onClose={handleCloseConfirmPublication}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            onConfirm={handleConfirmPublication}
          />
        )}
        {showRejectPublication && (
          <ConfirmModal
            open={showRejectPublication}
            title={t('modals.confirm-request-deletion')}
            message={t('modals.confirm-request-deletion-message')}
            simple
            onClose={handleCloseConfirmDeletion}
            onConfirm={handleConfirmDeletion}
          />
        )}
      </div>
    </>
  );
}
